/**
 * Phase 66 Plan 02 — react-router 7 Data Mode router.
 *
 * Path routing (NOT hash) per D-04: nginx.conf line 24 already has
 * `try_files $uri $uri/ /index.html` so the SPA fallback handles
 * deep-link reloads without code changes. Phase 65 hash URLs are
 * migrated by the synchronous shim in main.tsx (BEFORE createRoot —
 * RESEARCH §Pitfall 5).
 *
 * Route inventory mirrors PATTERNS.md §router table (D-09 grouped):
 *   /                       Dashboard
 *   /machines               Machines
 *   /machines/:cpuId        MachineDetail
 *   /provisioning           Provisioning
 *   /users                  Users
 *   /vpn                    VPN
 *   /settings               Settings
 *   /admin/files            AdminFiles (landing — Plan 05a expands MinIO/SrvFs/PG/Kiosk sub-routes)
 *   /admin/audit            AdminAudit
 *   /admin/circuits         AdminCircuits
 *
 * `handle.breadcrumb` populates <Breadcrumb> via useMatches(). All
 * authenticated routes nest under <RequireAuth /> via <Outlet>; the
 * top-level AppShell renders LoginPage when user is null instead of
 * redirecting (preserves deep-links across login).
 *
 * @see PATTERNS.md §`src/router.tsx`
 * @see RESEARCH.md §Pattern 1 — react-router 7 Data Mode
 */

import { Outlet, createBrowserRouter, Navigate } from "react-router";
import { lazy, Suspense } from "react";

import AppShell from "@/components/layout/AppShell";
import RequireAuth from "@/auth/RequireAuth";
import Can from "@/auth/Can";
import AccessDeniedPage from "@/pages/AccessDeniedPage";

/**
 * Wraps `React.lazy()` in a Suspense boundary so each lazy-loaded page
 * is independently chunked AND has a render-blocking fallback. Returns
 * a Component compatible with react-router 7's `Component` field.
 *
 * The fallback is intentionally minimal (existing `.loading-spinner`
 * class from Phase 65 CSS contract).
 */
function lazyRoute(
  factory: () => Promise<{ default: React.ComponentType }>,
): React.ComponentType {
  const LazyComp = lazy(factory);
  return function LazyRoute() {
    return (
      <Suspense fallback={<div className="loading-spinner">Caricamento…</div>}>
        <LazyComp />
      </Suspense>
    );
  };
}

const DashboardPage = lazyRoute(() => import("@/pages/DashboardPage"));
const MachinesPage = lazyRoute(() => import("@/pages/MachinesPage"));
const MachineDetailPage = lazyRoute(() => import("@/pages/MachineDetailPage"));
const ProvisioningPage = lazyRoute(() => import("@/pages/ProvisioningPage"));
const UsersPage = lazyRoute(() => import("@/pages/UsersPage"));
const VpnPage = lazyRoute(() => import("@/pages/VpnPage"));
const SettingsPage = lazyRoute(() => import("@/pages/SettingsPage"));
const AdminFilesPage = lazyRoute(() => import("@/pages/admin/AdminFilesPage"));
const AdminFilesMinIOPage = lazyRoute(() => import("@/pages/admin/AdminFilesMinIOPage"));
const AdminFilesSrvfsPage = lazyRoute(() => import("@/pages/admin/AdminFilesSrvfsPage"));
const AdminPgExportPage = lazyRoute(() => import("@/pages/admin/AdminPgExportPage"));
const AdminFilesKioskPage = lazyRoute(() => import("@/pages/admin/AdminFilesKioskPage"));
const AdminAuditPage = lazyRoute(() => import("@/pages/admin/AdminAuditPage"));
const AdminPowerHealthPage = lazyRoute(() => import("@/pages/admin/AdminPowerHealthPage"));
const AdminCircuitsPage = lazyRoute(() => import("@/pages/admin/AdminCircuitsPage"));
// Phase 69 Plan 05 — admin/operator user CRUD page.
const AdminUsersPageInner = lazyRoute(() => import("@/pages/admin/AdminUsersPage"));
/**
 * Route-level RBAC gate per UI-SPEC §7: operator / viewer / default admin
 * lacking admin_users:manage see <AccessDeniedPage />. Backend remains
 * authoritative on every /api/v1/admin-users endpoint.
 */
function AdminUsersPageRoute() {
  return (
    <Can permission="admin_users:manage" fallback={<AccessDeniedPage />}>
      <AdminUsersPageInner />
    </Can>
  );
}

// Phase 70 Plan 06 — Machine clone templates gallery page.
const AdminMachineTemplatesPageInner = lazyRoute(
  () => import("@/pages/admin/AdminMachineTemplatesPage"),
);
function AdminMachineTemplatesPageRoute() {
  return (
    <Can
      permission="machine_clone_templates:manage"
      fallback={<AccessDeniedPage />}
    >
      <AdminMachineTemplatesPageInner />
    </Can>
  );
}

export const router = createBrowserRouter([
  // Back-compat: legacy "/login" → home (login overlay renders there).
  { path: "/login", element: <Navigate to="/" replace /> },
  {
    Component: AppShell,
    children: [
      // Dashboard (index)
      {
        index: true,
        Component: DashboardPage,
        handle: { breadcrumb: "Panoramica" },
      },
      // Operations
      {
        path: "machines",
        Component: RequireAuth,
        handle: { breadcrumb: "Macchine" },
        children: [
          { index: true, Component: MachinesPage },
          {
            path: ":cpuId",
            Component: MachineDetailPage,
            handle: {
              breadcrumb: (params: Record<string, string | undefined>) =>
                params.cpuId ?? "",
            },
          },
        ],
      },
      {
        path: "provisioning",
        Component: RequireAuth,
        handle: { breadcrumb: "Provisioning" },
        children: [{ index: true, Component: ProvisioningPage }],
      },
      // Identity
      {
        path: "users",
        Component: RequireAuth,
        handle: { breadcrumb: "Utenti" },
        children: [{ index: true, Component: UsersPage }],
      },
      {
        path: "vpn",
        Component: RequireAuth,
        handle: { breadcrumb: "VPN" },
        children: [{ index: true, Component: VpnPage }],
      },
      // Settings
      {
        path: "settings",
        Component: RequireAuth,
        handle: { breadcrumb: "Impostazioni" },
        children: [{ index: true, Component: SettingsPage }],
      },
      // Admin
      {
        path: "admin",
        Component: RequireAuth,
        children: [
          // /admin/files (landing) + sub-routes (Plan 05a)
          {
            path: "files",
            Component: () => <Outlet />,
            handle: { breadcrumb: "File Manager" },
            children: [
              { index: true, Component: AdminFilesPage },
              {
                path: "minio/*",
                Component: AdminFilesMinIOPage,
                handle: { breadcrumb: "MinIO" },
              },
              {
                path: "srvfs/*",
                Component: AdminFilesSrvfsPage,
                handle: { breadcrumb: "Filesystem server" },
              },
              {
                path: "pg-export",
                Component: AdminPgExportPage,
                handle: { breadcrumb: "Export Postgres" },
              },
              {
                path: "kiosk/*",
                Component: AdminFilesKioskPage,
                handle: { breadcrumb: "Kiosk" },
              },
            ],
          },
          {
            path: "audit",
            Component: AdminAuditPage,
            handle: { breadcrumb: "Audit log" },
          },
          // Phase 69 Plan 05 — /admin/users (admin/operator user CRUD).
          {
            path: "users",
            Component: AdminUsersPageRoute,
            handle: { breadcrumb: "Utenti admin" },
          },
          // Phase 70 Plan 06 — /admin/machines/templates (clone templates gallery).
          {
            path: "machines/templates",
            Component: AdminMachineTemplatesPageRoute,
            handle: { breadcrumb: "Template clone" },
          },
          // Phase 68 Plan 03 — Power Health admin surface.
          // RBAC gating happens at <Can permission="power_health:read"> in the
          // Sidebar nav item; the route itself is mounted under <RequireAuth>
          // which already enforces login. Backend remains the final
          // authority on /api/v1/admin/power-health/* (admin role enforced).
          {
            path: "power-health",
            Component: AdminPowerHealthPage,
            handle: { breadcrumb: "Power Health" },
          },
          {
            path: "circuits",
            Component: AdminCircuitsPage,
            handle: { breadcrumb: "Circuiti" },
          },
        ],
      },
    ],
  },
]);
